import { Link } from "react-router-dom";
import { Briefcase, ArrowLeft } from "lucide-react";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-[#002FA7]" />
            <span className="font-black text-xl tracking-tight text-zinc-900">
              GetJobInterviews
            </span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-zinc-900 mb-8">Terms of Service & Privacy Policy</h1>
        
        <div className="prose prose-zinc max-w-none">
          {/* Terms of Service */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-zinc-900 mb-4">Terms of Service</h2>
            <p className="text-sm text-zinc-500 mb-6">Last updated: January 2026</p>
            
            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">1. Service Description</h3>
            <p className="text-zinc-700 mb-4">
              GetJobInterviews.com ("we", "us", "our") provides a job application assistance service. 
              We help job seekers by finding relevant job opportunities and preparing application materials 
              including job links, cover letter templates, and application guidance.
            </p>
            
            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">2. Service Expectations</h3>
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm mb-4">
              <p className="text-zinc-700 font-medium mb-2">Important Notice:</p>
              <ul className="list-disc list-inside text-zinc-700 space-y-1">
                <li>We focus on <strong>relevant job matches</strong>, not volume</li>
                <li>In some cases, you may receive fewer but <strong>higher-quality</strong> opportunities</li>
                <li>Job availability varies by role, location, and market conditions</li>
                <li>We do not guarantee interviews or job offers</li>
              </ul>
            </div>
            
            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">3. Delivery Timeline</h3>
            <p className="text-zinc-700 mb-4">
              We aim to deliver your job application package within <strong>3 business days</strong> of payment. 
              In cases requiring additional research or refinement, we will notify you of any delays.
            </p>
            
            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">4. Refund & Redo Policy</h3>
            <ul className="list-disc list-inside text-zinc-700 space-y-2 mb-4">
              <li><strong>One Free Refinement:</strong> If you're not satisfied with your initial batch, we'll refine it once for free</li>
              <li><strong>Partial Deliveries:</strong> If job availability is limited, we may deliver a partial batch with a follow-up</li>
              <li><strong>No Refunds After Delivery:</strong> Once materials are delivered, refunds are not available</li>
              <li><strong>Refunds Before Delivery:</strong> Full refund available if requested before work begins</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">5. What We Do NOT Guarantee</h3>
            <ul className="list-disc list-inside text-zinc-700 space-y-2 mb-4">
              <li>Job interviews or offers</li>
              <li>Exact number of jobs (depends on market availability)</li>
              <li>Response from employers</li>
              <li>Accuracy of third-party job listings</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">6. User Responsibilities</h3>
            <ul className="list-disc list-inside text-zinc-700 space-y-2 mb-4">
              <li>Provide accurate information about your background and preferences</li>
              <li>Review and customize templates before sending to employers</li>
              <li>Apply to jobs in a timely manner (job listings expire)</li>
              <li>Maintain professional conduct with potential employers</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">7. Payment Terms</h3>
            <p className="text-zinc-700 mb-4">
              All payments are processed securely through Razorpay. Prices are in USD and are one-time payments 
              (not subscriptions). By completing payment, you agree to these terms.
            </p>
          </section>

          {/* Privacy Policy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-zinc-900 mb-4">Privacy Policy</h2>
            
            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">1. Information We Collect</h3>
            <ul className="list-disc list-inside text-zinc-700 space-y-2 mb-4">
              <li><strong>Personal Information:</strong> Name, email, phone number</li>
              <li><strong>Professional Information:</strong> Target role, location preferences, LinkedIn URL</li>
              <li><strong>Payment Information:</strong> Processed by Razorpay (we don't store card details)</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">2. How We Use Your Information</h3>
            <ul className="list-disc list-inside text-zinc-700 space-y-2 mb-4">
              <li>To find and match relevant job opportunities</li>
              <li>To prepare personalized application materials</li>
              <li>To communicate about your order and delivery</li>
              <li>To improve our services</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">3. Data Retention</h3>
            <p className="text-zinc-700 mb-4">
              We retain your information for up to 12 months to provide follow-up support. 
              You can request deletion of your data at any time by contacting us.
            </p>
            
            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">4. Data Sharing</h3>
            <p className="text-zinc-700 mb-4">
              We do not sell your personal information. We may share data with:
            </p>
            <ul className="list-disc list-inside text-zinc-700 space-y-2 mb-4">
              <li>Payment processors (Razorpay) for transaction processing</li>
              <li>Email service providers for delivery communications</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-zinc-900 mt-6 mb-3">5. Your Rights</h3>
            <ul className="list-disc list-inside text-zinc-700 space-y-2 mb-4">
              <li>Access your personal data</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Opt out of marketing communications</li>
            </ul>
          </section>

          {/* Disclaimers */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-zinc-900 mb-4">Disclaimers</h2>
            
            <div className="bg-zinc-100 border border-zinc-200 p-4 rounded-sm mb-4">
              <h3 className="text-lg font-semibold text-zinc-900 mb-3">Employment Disclaimer</h3>
              <p className="text-zinc-700">
                GetJobInterviews.com is NOT an employment agency, recruiter, or staffing company. 
                We provide job search assistance tools and materials only. We do not employ, place, 
                or guarantee employment for any user. All job applications are made directly by the 
                user to the hiring company.
              </p>
            </div>
            
            <div className="bg-zinc-100 border border-zinc-200 p-4 rounded-sm mb-4">
              <h3 className="text-lg font-semibold text-zinc-900 mb-3">Third-Party Job Listings</h3>
              <p className="text-zinc-700">
                Job listings are sourced from third-party job boards and company websites. We do not 
                control the accuracy, availability, or terms of these listings. Jobs may be filled, 
                removed, or changed without notice. Always verify job details directly with employers.
              </p>
            </div>
            
            <div className="bg-zinc-100 border border-zinc-200 p-4 rounded-sm mb-4">
              <h3 className="text-lg font-semibold text-zinc-900 mb-3">No Guarantee of Results</h3>
              <p className="text-zinc-700">
                Success in job applications depends on many factors beyond our control, including your 
                qualifications, market conditions, employer preferences, and timing. We provide tools 
                and guidance to improve your chances but cannot guarantee specific outcomes.
              </p>
            </div>
            
            <div className="bg-zinc-100 border border-zinc-200 p-4 rounded-sm mb-4">
              <h3 className="text-lg font-semibold text-zinc-900 mb-3">Template Usage</h3>
              <p className="text-zinc-700">
                Cover letters and email templates are starting points and should be customized by you. 
                We recommend personalizing all materials before sending to potential employers. 
                You are responsible for the final content of your applications.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-zinc-900 mb-4">Contact Us</h2>
            <p className="text-zinc-700 mb-4">
              For questions about these terms or our services:
            </p>
            <ul className="list-none text-zinc-700 space-y-2">
              <li><strong>Email:</strong> support@getjobinterviews.com</li>
              <li><strong>Contact Form:</strong> <Link to="/contact" className="text-[#002FA7] hover:underline">Click here</Link></li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-zinc-900 text-white mt-12">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center">
          <p className="text-sm text-zinc-400">
            © {new Date().getFullYear()} GetJobInterviews.com. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
